/* Copyright (c) 2017 Backpack Health LLC */

/* global window */

import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import './FilePreview.scss';

import ImageUtil from '../../../utilities/ImageUtil';
import AppUtils from '../../../utilities/AppUtils';
import FileModel from '../../../models/FileModel';
import MobilePdfViewer from '../../components/MobilePdfViewer';
import { isMobileApplication } from '../../../utilities/MobileUtil';
import PdfViewer from '../components/PdfViewer';
import { useUIState } from '../../hooks/useUIState';
import { useActiveProfile } from '../../hooks/useProfile';
import ProfileModel from '../../../models/ProfileModel';
import { Card, Icons } from '../../../bphComponents/bundle';
import COLORS from '../../styles/Colors';
import { ProfileThemedBasePrimaryButton } from '../components/BackpackButtons';
import { ContentWrapperNoTopMargin } from '../../components/ContentWrapper';

function getOtherMessage(isMainProfile, profileName) {
  let phraseId = '';
  if (AppUtils.isMobileDevice()) {
    if (isMainProfile) {
      phraseId = 'file.userExportToView.message.text';
    } else {
      phraseId = 'file.notUserExportToView.message.text';
    }
  } else if (isMainProfile) {
    phraseId = 'file.userDownloadToView.message.text';
  } else {
    phraseId = 'file.notUserDownloadToView.message.text';
  }

  return {
    id: phraseId,
    values: isMainProfile ? {} : { string1: profileName },
  };
}
const OtherFile = (props) => {
  const Icon = props.icon;
  const [uiState] = useUIState();
  const [profileData] = useActiveProfile();
  const profile = new ProfileModel(profileData.data);
  const messagePhrase = getOtherMessage(uiState.isActiveProfileMainProfile, profile.greetingName());

  return (
    <div style={{ marginTop: '150px' }}>
      <div
        style={{
          justifyContent: 'space-between',
          verticalAlign: 'center',
          alignItems: 'center',
        }}
      >
        <div
          style={{ width: '220px', margin: '0 auto', cursor: 'pointer' }}
          onClick={props.download}
        >
          <div style={{ margin: '0 auto 8px', width: '30px' }}>
            <Icon color="inactive" />
          </div>
          <div className="font-caption" style={{ textAlign: 'center' }}>
            heyo
            <FormattedMessage {...messagePhrase} />
          </div>
        </div>
      </div>
    </div>
  );
};

interface iProps {
  attemptDownload(url): void;
  shareFile(u): void;
  downloadUrl: string;
  previewUrl: string;
  file: FileModel;
  profile: any;
  style?: any;
}

function getErrorMessage(profile) {
  if (AppUtils.isMobileDevice()) {
    return profile.isDependent() ? {
      id: 'file.notUserExportToView.message.text',
      values: { string1: profile.greetingName() },
    } : {
      id: 'file.userExportToView.message.text',
    };
  } else {
    return profile.isDependent() ? {
      id: 'file.notUserDownloadToView.message.text',
      values: { string1: profile.greetingName() },
    } : {
      id: 'file.userDownloadToView.message.text',
    };
  }
}
const FilePreview: React.FC<iProps> = (props) => {
  const { previewUrl, file, profile, attemptDownload, downloadUrl, shareFile } = props;
  const style = props.style || {};

  const [state, setState] = React.useState({
    previewImageLoaded: false,
    styles: {
      maxWidth: window.innerWidth - 50,
      maxHeight: window.innerHeight - 250,
      transform: null,
    },
    previewImageStyles: {},
  });

  function downloadFile(e) {
    e.preventDefault();
    attemptDownload(downloadUrl);
  }

  function orientImage(url) {
    ImageUtil.arrayBufferFromUrl(url, image => {
      ImageUtil.imageOrientation(image, orientation => {
        let transform = 'rotate(0deg)';
        switch (orientation) {
          case 3:
            transform = 'rotate(180deg)';
            break;
          case 6:
            transform = 'rotate(90deg)';
            break;
          case 8:
            transform = 'rotate(270deg)';
            break;
          default:
        }
        transform = AppUtils.isIosDevice() ? 'rotate(0deg)' : transform;
        const imgStyles = state.styles;
        imgStyles.transform = transform;
        setState({
          previewImageStyles: { width: '100%', height: '100%' },
          previewImageLoaded: true,
          styles: state.styles,
        });
      });
    });
  }

  React.useEffect(() => {
    if (previewUrl && file.isImage()) {
      orientImage(previewUrl);
    }
  }, [props.previewUrl]);

  if (!previewUrl) { return null; }

  const styleClass = classnames('cmp-filepreview', {
    'pdf-viewer': file.isPdf(),
    'pdf-mobile-viewer': isMobileApplication(),
  });

  return file.isPreviewAvailable() ? (
    <div className={styleClass} style={{ ...style, paddingBottom: file.isPdf() ? null : style.paddingBottom }}>
      <Media
        {...props}
        previewImageLoaded={state.previewImageLoaded}
        styles={state.styles}
        downloadFile={downloadFile}
      />
    </div>
  ) : (
    <div className="cmp-filepreview__missing" style={style}>
      <ContentWrapperNoTopMargin style={{ paddingBottom: 0 }}>
        <Card.CardContent disabledPadding={['bottom']}>
          <div style={{ marginBottom: 24 }}>
            <Icons.IconFileTypeOther.XLarge color={COLORS.gsDark} />
          </div>

          <div className="font-subhead color-gsDarkest" style={{ marginBottom: 24 }}>
            <FormattedMessage {...getErrorMessage(profile)} />
          </div>
          <ProfileThemedBasePrimaryButton onClick={isMobileApplication() ? shareFile : downloadFile} style={{ textAlign: 'center' }}>
            <FormattedMessage id={AppUtils.isMobileDevice() ? 'file.export.button.label' : 'file.download.button.label'} />
          </ProfileThemedBasePrimaryButton>
        </Card.CardContent>
      </ContentWrapperNoTopMargin>
    </div>
  );
};

const Media = (props) => {
  const { file, previewUrl, downloadUrl, previewImageLoaded, styles, profile, downloadFile } = props;
  const profileId = profile.data.id;

  const mimeType = file.getMimeType();
  const url = previewUrl;

  if (file.isImage()) {
    if (previewImageLoaded) {
      return (
        <img
          alt={file.data.name}
          src={url}
          style={{ width: '100%' }}
        />
      );
    }
    return null;
  } else if (file.isVideo()) {
    return (
      <video controls width="100%" height="100%" style={styles}>
        <source src={url} type={mimeType} />
      </video>
    );
  } else if (file.isAudio()) {
    return (
      <audio  controls style={styles}>
        <source src={url} type={mimeType} />
      </audio>
    );
  } else if (file.isPdf()) {
    return isMobileApplication() ? (
      <MobilePdfViewer
        url={downloadUrl}
        fileName={file.data.name}
        noHeader={true}
        noCache={false}
        footerHeight={20}
        close={() => {}}
        profileId={profileId}
      />
    ) : (
      <PdfViewer path={downloadUrl} hideToolbar />
    );
  } else if (file.isHtml()) {
    return (
      <iframe
        src={url}
        sandbox="true"
        style={styles}
      />
    );
  } else {
    return (
      <OtherFile download={downloadFile} icon={file.icon()} />
    );
  }
};

export default FilePreview;
